import { Grid, Col, Row } from "react-bootstrap";
import WaitlistStats from "components/WaitlistStats/WaitlistStats";
import DashboardTable from "components/DashboardTable/DashboardTable";
import { useEffect } from "react";
import { getAccountById } from "services/account/account.service";
import { useState } from "react";
import { useSelector } from "react-redux";
import MultiSelectFilter from "components/MultiSelectFilter/MultiSelectFilter";
import { expandAreaIds } from "services/access/accessResolver";

function Dashboard(props) {
  const aggregateSelector = useSelector(state => state.aggregate);
  const visitSelector = useSelector(state => state.visit);
  const businesses = useSelector(state => state.business.businesses);
  const regions = useSelector(state => {
    return state.regions.regions;
  });
  const { dashboardStats } = aggregateSelector;
  const { waitingVisitors } = visitSelector;
  const [account, setAccount] = useState(null);
  const [userBusiness, setUserBusiness] = useState(null);
  const [areaValues, setAreaValues] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState(businesses);
  const [businessesMap, setBusinessesMap] = useState();
  const [filterLoading, setFilterLoading] = useState(true);
  const [dashboardFilterOptions, setDashboardFilterOptions] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);

  const isRegionalManager = props.user.claims.role === "regional-manager";
  const isAllLocations = !!(props.user.access && props.user.access.allLocations);

  // Access-based pruning replaces the legacy regional-manager-only trim.
  // When `access.allLocations === true` (or no `access` map yet, for
  // pre-migration users) the full option tree passes through. When the user
  // has an explicit `access` map, the tree is pruned to the accessible
  // regions/areas by joining against the current regions, mirroring the
  // resolver's cascade (regions -> areas -> businesses).
  const ifAccessThanFilter = options => {
    const access = props.user.access;
    if (!access || access.allLocations) {
      return options;
    }
    const regionIdSet = new Set(access.regionIds || []);
    const areaIdSet = expandAreaIds(access.areaIds, access.regionIds, regions);
    // This filter tree only selects at area granularity — an individually
    // granted business (access.businessIds, no region/area checked) has no
    // node of its own, so include its area too or the area never becomes
    // selectable and the (already access-scoped) business list underneath
    // it never gets shown.
    const businessIdSet = new Set(access.businessIds || []);
    if (businessIdSet.size > 0) {
      businesses.forEach(b => {
        if (businessIdSet.has(b.businessId) && b.regionId) {
          areaIdSet.add(b.regionId);
        }
      });
    }
    return options
      .map(opt => {
        // Region row — keep if granted, else keep only its accessible children.
        if (regionIdSet.has(opt.value)) {
          return opt;
        }
        const children = (opt.children || []).filter(child => areaIdSet.has(child.value));
        if (children.length === 0) {
          return null;
        }
        return { ...opt, children };
      })
      .filter(Boolean);
  };

  useEffect(() => {
    const loadAccount = async () => {
      const account = await getAccountById(props.user.accountId);
      setAccount(account);
    };
    loadAccount();
  }, [props.user.accountId]);

  useEffect(() => {
    if (businesses) {
      setUserBusiness(businesses.find(b => props.user.businessId === b.businessId));
      let buses = businesses;

      if (areaValues && areaValues.length) {
        buses = businesses.filter(
          business => business.dashboardEnabled === true && selectedAreas.some(area => area.value === business.regionId)
        );
      }

      let businessesMap = {};
      buses.forEach(b => {
        businessesMap[b.businessId] = b;
      });
      setBusinessesMap(businessesMap);
      setFilteredBusinesses(buses);
    }
  }, [businesses, selectedAreas]);

  const onFilter = selectedOptions => {
    let options = ifAccessThanFilter(selectedOptions);
    const areas = options.map(so => so.children.filter(c => c.checked)).flat();
    setSelectedAreas(areas);
  };

  // Derives areaValues AND builds the filter tree in one pass, keyed
  // directly on `regions` (the actual Redux data) rather than on a
  // locally-derived "loading" boolean. Splitting this into two effects
  // chained through a `regionLoading` flag was the bug behind #11: React 18
  // batches the flag's set-true-then-false inside the same effect call into
  // a single render, so once `regionLoading` had already settled at `false`
  // (from an earlier run with no data yet), a later run with real data could
  // net `false -> false` — no visible change — and the effect depending on
  // `[regionLoading]` would silently never re-fire, leaving the Dashboard
  // permanently empty until a remount. `regions` itself is always a genuine
  // new reference when new data arrives, so keying on it directly can't lose
  // the transition the same way.
  useEffect(() => {
    if (!regions) return;
    setFilterLoading(true);

    const areas = regions
      .filter(r => r.isArea)
      .map(r => ({ value: r.id, label: r.name, parentRegion: r.parentRegion }));
    const allRegions = regions.filter(r => !r.isArea).map(r => ({ value: r.id, label: r.name }));
    setAreaValues(areas);

    if (!areas.length) {
      setFilterLoading(false);
      return;
    }

    const filterOptions = [...allRegions.map(r => ({ ...r, checked: true, children: [] }))];
    filterOptions.push({ value: "without-region", label: "Without Region", checked: true, children: [] });
    areas.forEach(a => {
      let parentIndex = filterOptions.findIndex(o => o.value === a.parentRegion);
      const isWithParent = parentIndex > -1;
      parentIndex = isWithParent ? parentIndex : filterOptions.length - 1;
      filterOptions[parentIndex].children.push({
        ...a,
        checked: true,
        parentRegion: isWithParent ? a.parentRegion : "without-region"
      });
    });
    setDashboardFilterOptions(ifAccessThanFilter(filterOptions));

    const filtersFromLS = JSON.parse(localStorage.getItem("dashboard_filters"));
    if (filtersFromLS) {
      const values = [...filterOptions];
      const newFilteredValues = values.map(parent => {
        return {
          ...parent,
          children:
            parent?.children.map(child => {
              return {
                ...child,
                checked:
                  filtersFromLS.find(fls => fls.value === parent.value)?.children.find(c => c.value === child.value)
                    ?.checked ??
                  (child?.checked || true)
              };
            }) || []
        };
      });

      newFilteredValues.forEach((p, parentIndex) => {
        let isAllChildrenChecked = true;
        let isAllChildrenUnChecked = true;

        newFilteredValues[parentIndex].children.forEach(c => {
          c.checked ? (isAllChildrenUnChecked = false) : (isAllChildrenChecked = false);
        });

        if (isAllChildrenChecked) newFilteredValues[parentIndex].checked = true;
        else if (isAllChildrenUnChecked || !isAllChildrenChecked) {
          newFilteredValues[parentIndex].checked = false;
        }
      });

      setDashboardFilterOptions(ifAccessThanFilter(newFilteredValues));
      onFilter(newFilteredValues);
    } else onFilter(filterOptions);
    setFilterLoading(false);
  }, [regions]);

  return (
    <div className="dashboard main-content">
      <div className="stats-container">
        {waitingVisitors ? (
          <WaitlistStats
            isDashboard={true}
            business={userBusiness}
            dashboardStats={dashboardStats}
            businesses={businessesMap}
            user={props.user}
          />
        ) : (
          ""
        )}
        {/* The Dashboard filter shows whenever the user has more than one accessible
            business OR an `allLocations` grant. Roles permission-tier this view
            (the legacy role list is retained for pre-migration users), but the
            access map decides whether the tree is worth rendering at all. */}
        {(props.user.claims.role === "admin" ||
          props.user.claims.role === "regional-manager" ||
          props.user.claims.role === "area-manager" ||
          props.user.claims.role === "analyst" ||
          isAllLocations ||
          (props.user.access &&
            (props.user.access.regionIds || []).length +
              (props.user.access.areaIds || []).length +
              (props.user.access.businessIds || []).length >
              1)) && (
          <>
            {!filterLoading && (
              <MultiSelectFilter
                showOnlyChidren={isRegionalManager}
                onFilter={onFilter}
                options={dashboardFilterOptions}
                filteredText="Dashboard filtered."
              />
            )}
          </>
        )}

        <div>
          {userBusiness && businesses && businesses.length && dashboardStats && account ? (
            <DashboardTable user={props.user} businesses={filteredBusinesses} dashboardStats={dashboardStats} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
